import { Component, computed, inject, output, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, interval, merge, Observable } from 'rxjs';
import { exhaustMap, filter, map, startWith, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { QuestionSectionDto } from '../../../../../model/exam/question-section-dto';
import { SignalrService } from '../../../../../service/signalr/signalr.service';
import { SynchroniseService } from '../../../../../service/synchronise.service';
import { TimeService } from '../../../../../service/time.service';
import { MatIcon } from '@angular/material/icon';

@Component({
	selector: 'app-exam-timer',
	templateUrl: './exam-timer.component.html',
	styleUrls: ['./exam-timer.component.scss'],
	imports: [MatIcon]
})
export class ExamTimerComponent {
	private readonly timeService: TimeService = inject(TimeService);
	private readonly signalRService: SignalrService = inject(SignalrService);
	private readonly synchroniseService: SynchroniseService = inject(SynchroniseService);

	public readonly timerRedirectAction = output<string>();

	/** The milliseconds that stay in the section, or null while its finish time loads. */
	private readonly remaining: WritableSignal<number | null> = signal(null);

	/** The clock of the header, for example `01:29:57`. */
	protected readonly clock: Signal<string> = computed(() => this.toClock(this.remaining() ?? 0));

	private readonly addedTime$: Observable<unknown> = this.signalRService.on<QuestionSectionDto[]>('time')
		.pipe(switchMap((sections: QuestionSectionDto[]) => this.timeService.addTimeToCurrentSection(sections)));

	/**
	 * Each emission counts the section that runs now down again: at the start, when the exam
	 * moves to a new section, and when the invigilator adds time.
	 *
	 * The exam keeps the same route from one section to the next, so a section change does not
	 * always make a new component. This is why the restart comes from the service and not
	 * from a lifecycle hook.
	 */
	private readonly restart$: Observable<unknown> = merge(this.timeService.startTimerChange$, this.addedTime$)
		.pipe(startWith(null));

	constructor() {
		this.restart$
			.pipe(
				// switchMap stops the countdown of the section before this one, so one clock runs.
				switchMap(() => this.currentSectionCountdown()),
				tap((remaining: number) => this.remaining.set(Math.max(remaining, 0))),
				filter((remaining: number) => remaining <= 0),
				// The move is not immediate, and exhaustMap keeps it to one move at a time.
				exhaustMap(() => this.moveToNextBlock()),
				filter((url: string) => !!url),
				takeUntilDestroyed()
			)
			.subscribe((url: string) => this.timerRedirectAction.emit(url));
	}

	/**
	 * Gives the milliseconds that stay in the section that runs now, once each second. The
	 * last value is 0 or less, which tells the exam to move to the next block.
	 */
	private currentSectionCountdown(): Observable<number> {
		return forkJoin({
			finish: this.timeService.getCurrentSectionTime(),
			serverTime: this.timeService.resolveCurrentTime()
		}).pipe(
			switchMap(({ finish, serverTime }: { finish: Date, serverTime: Date }) => {
				const finishTime: number = new Date(finish).getTime();
				const serverClientOffset: number = Date.now() - new Date(serverTime).getTime();

				return interval(1000)
					.pipe(startWith(0))
					.pipe(map(() => finishTime - (Date.now() - serverClientOffset)));
			}),
			takeWhile((remaining: number) => remaining > 0, true),
			takeUntil(this.timeService.stopTimerChange$)
		);
	}

	private moveToNextBlock(): Observable<string> {
		return this.timeService.nextBlock()
			.pipe(switchMap((url: string) => this.synchroniseService.calculateChecksum().pipe(map(() => url))));
	}

	private toClock(remaining: number): string {
		const time: Date = new Date(remaining);

		return [time.getUTCHours(), time.getUTCMinutes(), time.getUTCSeconds()]
			.map((part: number) => part.toString().padStart(2, '0'))
			.join(':');
	}
}
